import React, { useState, useRef, useEffect } from "react";
import { PaymentCalendarProps, CalendarDay } from "../../types/invoice.types";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Calendar } from "primereact/calendar";

// Extended props with onMonthChange callback
interface ExtendedPaymentCalendarProps extends PaymentCalendarProps {
  onMonthChange?: (month: number, year: number) => void;
  setCurrentMonth: any;
  currentMonth: string;
  currentYear: number;
}

const PaymentCalendar: React.FC<ExtendedPaymentCalendarProps> = ({
  month,
  year,
  calendarData,
  today,
  onMonthChange,
  currentYear,
  currentMonth,
  setCurrentMonth,
}) => {
  const [currentView, setCurrentView] = useState<
    "dayGridMonth" | "dayGridWeek"
  >("dayGridMonth");
  const calendarRef = useRef<any>(null);
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    new Date(currentYear, monthNames.indexOf(currentMonth), 1)
  );

  // Keep track of the actual current date (for "Today" marker)
  const [actualToday] = useState(() => {
    const now = new Date();
    return {
      day: now.getDate(),
      month: now.getMonth() + 1, // 1-based month
      year: now.getFullYear(),
    };
  });

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get month number from name
  const getMonthNumber = (monthName: string) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months.indexOf(monthName) + 1;
  };

  const monthNumber = getMonthNumber(month);

  // Convert calendarData to FullCalendar events
  const events = calendarData.flatMap((day) => {
    if (!day.payments || day.payments.length === 0) return [];

    return day.payments.map((payment, index) => {
      // Create a date string in YYYY-MM-DD format
      const date = `${year}-${String(monthNumber).padStart(2, "0")}-${String(
        day.day
      ).padStart(2, "0")}`;

      // Determine background color based on status
      let backgroundColor = "#f0f5fa"; // Default light blue for pending
      let textColor = "#333333"; // Dark gray text for better contrast on light backgrounds

      if (payment.status === "paid") {
        backgroundColor = "#e6f5ec"; // Light green for paid
      } else if (payment.status === "overdue") {
        backgroundColor = "#ffeaee"; // Light salmon for overdue
      }

      return {
        id: `payment-${day.day}-${index}`,
        title: formatCurrency(payment.amount),
        start: date,
        allDay: true,
        backgroundColor,
        borderColor: "transparent",
        textColor,
        display: "block",
        extendedProps: {
          status: payment.status,
        },
      };
    });
  });

  // Custom calendar content rendering
  const dayCellContent = (arg: any) => {
    const dayNumber = arg.dayNumberText;
    const date = arg.date;

    // Check if this is today - only if we're in the current month AND year AND day
    const isToday =
      date.getDate() === actualToday.day &&
      date.getMonth() + 1 === actualToday.month &&
      date.getFullYear() === actualToday.year;

    // Check if this day is not in the current month
    const isOtherMonth =
      date.getMonth() + 1 !== monthNumber || date.getFullYear() !== year;

    return (
      <div className="text-right text-xs">
        {isToday ? (
          <div className="font-medium">{dayNumber}</div>
        ) : (
          <div className={isOtherMonth ? "text-gray-400" : ""}>{dayNumber}</div>
        )}
        {isToday && (
          <div className="text-xs text-center mt-3 font-medium">Today</div>
        )}
      </div>
    );
  };

  const renderEventContent = (eventInfo: any) => {
    const { event } = eventInfo;
    const status = event.extendedProps?.status;

    if (!status) return null; // Skip special events

    // Circle colors based on status
    let circleColor = "";
    let statusLetter = "";
    if (status === "paid" || status == "partial") {
      circleColor = "bg-green-600";
      statusLetter = "P";
    } else if (status === "pending") {
      circleColor = "bg-amber-500";
      statusLetter = "P";
    } else if (status === "overdue") {
      circleColor = "bg-red-600";
      statusLetter = "O";
    } else if (status === "write_off") {
      circleColor = "bg-teal-600";
      statusLetter = "W";
    }

    return (
      <div className="flex items-center gap-2 mt-1">
        <div
          className={`h-5 w-5 rounded-full ${circleColor} border border-white flex items-center justify-center`}
        >
          <span className="text-xs font-bold text-white">{statusLetter}</span>
        </div>
        <div className="text-xs font-medium">{event.title}</div>
      </div>
    );
  };

const handlePrevMonth = () => {
  if (calendarRef.current) {
    const api = calendarRef.current.getApi();
    api.prev(); // Go to previous month/week
    
    // Get the new date
    const currentDate = api.getDate();
    // Set the selected date
    setSelectedDate(currentDate);
    
    const newMonth = currentDate.getMonth() + 1; // 1-based month
    const newYear = currentDate.getFullYear();
    setCurrentMonth(newMonth);
    
    // Notify parent component about month change
    if (onMonthChange) {
      onMonthChange(newMonth, newYear);
    }
  }
};

const handleNextMonth = () => {
  if (calendarRef.current) {
    const api = calendarRef.current.getApi();
    api.next(); // Go to next month/week
    
    // Get the new date
    const currentDate = api.getDate();
    // Set the selected date
   
    setSelectedDate(currentDate);
    
    const newMonth = currentDate.getMonth() + 1; // 1-based month
    const newYear = currentDate.getFullYear();
    setCurrentMonth(newMonth);
    
    // Notify parent component about month change
    if (onMonthChange) {
      onMonthChange(newMonth, newYear);
    }
  }
};

  // View change handler
  const handleViewChange = (newView: "dayGridMonth" | "dayGridWeek") => {
    setCurrentView(newView);
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      api.changeView(newView);
      // Reset to initial date to ensure we're looking at the correct month/week
      api.gotoDate(`${year}-${String(monthNumber).padStart(2, "0")}-01`);
    }
  };

  // New handler for month picker change
  const handleMonthSelection = (e: any) => {
    if (e.value) {
      const newDate = new Date(e.value);
      const newMonth = newDate.getMonth() + 1; // 1-based month
      const newYear = newDate.getFullYear();

      setSelectedDate(newDate);

      // Update calendar view
      if (calendarRef.current) {
        const api = calendarRef.current.getApi();
        api.gotoDate(`${newYear}-${String(newMonth).padStart(2, "0")}-01`);
      }

      setCurrentMonth(newMonth);

      // Notify parent component about month change
      if (onMonthChange) {
        onMonthChange(newMonth, newYear);
      }
    }
  };

  // Keep the current view when component updates
  // useEffect(() => {
  //   if (calendarRef.current) {
  //     const api = calendarRef.current.getApi();
  //     api.changeView(currentView);
  //   }
  // }, [month, year, currentView]);
  const monthNameToIndex = (name: string) => {
  return new Date(`${name} 1, 2024`).getMonth(); 
};
useEffect(() => {
  if (!calendarRef.current) return;
  if (!month || !year) return;

  const api = calendarRef.current.getApi();

  // Convert "December" → 11
  const monthIndex = monthNames.indexOf(currentMonth)

  const newDate = new Date(Number(year), monthIndex, 1);

  if (isNaN(newDate.getTime())) return;

  setSelectedDate(newDate);
  api.gotoDate(newDate);
  api.changeView(currentView);

}, [month, year, currentView]);


  return (
    <div className="p-3">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold text-gray-800">
          {month} {year}
        </h2>
        <div className="flex items-center space-x-2">
          {/* Improved Month/Week toggle buttons */}
          <div className="rounded-md p-0.5 flex">
            <Calendar
              value={selectedDate}
              onChange={handleMonthSelection}
              view="month"
              dateFormat="mm/yy"
            />
          </div>

          {/* Navigation buttons - now enabled for both views */}
          <div className="flex space-x-1">
            <button
              className="p-1.5 rounded-md border-gold text-gray-700 hover:bg-gold bg-transparent text-gold hover:text-white border-1 transition-colors flex items-center justify-center cursor-pointer p-button"
              onClick={handlePrevMonth}
              aria-label="Previous month"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button
              className="p-1.5 rounded-md border-gold text-gray-700 hover:bg-gold bg-transparent text-gold hover:text-white border-1 transition-colors flex items-center justify-center cursor-pointer p-button"
              onClick={handleNextMonth}
              aria-label="Next month"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView={currentView}
        headerToolbar={false} // Hide default header
        events={events}
        eventContent={renderEventContent}
        dayCellContent={dayCellContent}
        height="auto"
        fixedWeekCount={false}
        dayMaxEvents={3}
        firstDay={0} // Start with Sunday
        initialDate={`${year}-${String(monthNumber).padStart(2, "0")}-01`}
        // Remove validRange to allow navigation between months
        dayCellClassNames={(arg) => {
          const date = arg.date;
          // Use the actual today's date for highlighting
          const isToday =
            date.getDate() === actualToday.day &&
            date.getMonth() + 1 === actualToday.month &&
            date.getFullYear() === actualToday.year;

          if (isToday) {
            return "bg-blue-50 border-blue-300";
          }
          return "";
        }}
        eventClick={(info) => {
          // Handle event click - could show payment details in the future
          console.log("Payment clicked:", info.event.title);
        }}
      />
    </div>
  );
};

export default PaymentCalendar;
